// ScatterPlot.js
import { useCallback, useEffect, useMemo, useState } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import { colors } from "../utils";

const Plot = createPlotlyComponent(Plotly);

const ScatterPlot = ({
  isLoading,
  data, // EXPECTED: data keyed by node uuid (value)
  timebucketIdx, // proxy bucket index
  activeDomains,
  activeEntities,
  activeProxies,
}) => {
  const [chartData, setChartData] = useState(null);
  const [chartLayout, setChartLayout] = useState(null);
  const [showPlot, setShowPlot] = useState(false);

  const proxyIdSet = useMemo(() => {
    return new Set((activeProxies ?? []).map((p) => p.value));
  }, [activeProxies]);

  const createChartLayout = useCallback(() => {
    const padding = 5;
    const layout = {
      scatter: { bgcolor: "rgba(0,0,0,0)" },
      title: "",
      cliponaxis: false,
      xaxis: {
        title: {
          text: activeDomains?.length === 2 ? activeDomains[0]?.label : "",
          font: { size: 14, color: "white" },
          standoff: 20,
        },
        range: [-padding, 100 + padding],
        linecolor: "white",
        gridcolor: "rgba(255,255,255, 0.1)",
        automargin: true,
      },
      yaxis: {
        title: {
          text: activeDomains?.length === 2 ? activeDomains[1]?.label : "",
          font: { size: 14, color: "white" },
          standoff: 20,
        },
        range: [-padding, 100 + padding],
        linecolor: "white",
        gridcolor: "rgba(255,255,255, 0.1)",
        automargin: true,
      },
      width: 840, // Plotly prefers numbers
      height: 750, // Plotly prefers numbers
      paper_bgcolor: "transparent",
      plot_bgcolor: "transparent",
      font: { color: "white" },
      margin: { // Add this for extra control
        l: 60,
        r: 30,
        t: 30,
        b: 60,
        pad: 10
      }
    };

    setChartLayout(layout);
  }, [activeDomains]);

  const createChartData = useCallback(() => {
    if (!data) return;
    if (!activeDomains || activeDomains.length < 2) return;

    const nodes = [...(activeEntities ?? []), ...(activeProxies ?? [])];
    const safeProxyBucketIdx =
      typeof timebucketIdx === "number" ? timebucketIdx : -1;

    const d0 = activeDomains[0];
    const d1 = activeDomains[1];

    const plotData = nodes
      .map((node, idx) => {
        const nodeId = node?.value; // uuid
        const nodeName = node?.label; // display name
        const isProxy = proxyIdSet.has(nodeId);

        if (!nodeId || !nodeName) return null;
        if (!data[nodeId]) return null;

        const bucketIdx = isProxy ? safeProxyBucketIdx : 0;
        if (isProxy && bucketIdx < 0) return null;

        const sX = data?.[nodeId]?.[d0?.value];
        const sY = data?.[nodeId]?.[d1?.value];

        const scoreX = sX?.value?.[bucketIdx];
        const lbX = sX?.value_lb?.[bucketIdx];
        const ubX = sX?.value_ub?.[bucketIdx];

        const scoreY = sY?.value?.[bucketIdx];
        const lbY = sY?.value_lb?.[bucketIdx];
        const ubY = sY?.value_ub?.[bucketIdx];

        // If any of the main values is missing, skip
        if (
          scoreX === undefined ||
          lbX === undefined ||
          ubX === undefined ||
          scoreY === undefined ||
          lbY === undefined ||
          ubY === undefined
        ) {
          return null;
        }

        const deltaLbX = scoreX - lbX;
        const deltaUbX = ubX - scoreX;

        const deltaLbY = scoreY - lbY;
        const deltaUbY = ubY - scoreY;

        const intervals = [
          `${scoreX} [${lbX}, ${ubX}]`,
          `${scoreY} [${lbY}, ${ubY}]`,
        ];

        const fillColor = isProxy
          ? "rgba(255,255,255,0.4)"
          : colors[idx % colors.length]
              .replace("rgb", "rgba")
              .replace(")", ",0.4)");

        const lineColor = isProxy
          ? "rgba(255,255,255,1)"
          : colors[idx % colors.length];

        return {
          x: [scoreX],
          y: [scoreY],
          error_x: {
            type: "data",
            array: [deltaUbX],
            arrayminus: [deltaLbX],
            visible: true,
            thickness: 2,
            width: 4,
            color: fillColor,
          },
          error_y: {
            type: "data",
            array: [deltaUbY],
            arrayminus: [deltaLbY],
            visible: true,
            thickness: 2,
            width: 4,
            color: fillColor,
          },
          mode: "markers",
          type: "scatter",
          marker: {
            size: 8,
            color: lineColor,
          },
          customdata: [intervals],
          name: nodeName,
          hovertemplate: `<b>%{data.name}</b><br>${d0.label}: %{customdata[0]}<br>${d1.label}: %{customdata[1]}<extra></extra>`,
        };
      })
      .filter(Boolean);

    setChartData(plotData);
  }, [activeDomains, activeEntities, activeProxies, data, proxyIdSet, timebucketIdx]);

  useEffect(() => {
    const hasNodes =
      (activeProxies?.length ?? 0) > 0 || (activeEntities?.length ?? 0) > 0;

    if (
      isLoading ||
      !data ||
      !hasNodes ||
      (activeDomains?.length ?? 0) < 2
    ) {
      setShowPlot(false);
      setChartLayout(null);
      setChartData(null);
      return;
    }

    setShowPlot(true);
    createChartData();
    createChartLayout();
  }, [
    activeDomains,
    activeEntities,
    activeProxies,
    data,
    createChartData,
    createChartLayout,
    isLoading,
    timebucketIdx,
  ]);

  return (
    <div className="flex flex-col items-center content-center min-w-50">
      {isLoading && <p className="text-center">Loading data...</p>}
      {!isLoading && chartLayout && chartData && showPlot && (
        <Plot data={chartData} layout={chartLayout} />
      )}
    </div>
  );
};

export default ScatterPlot;